/**
 * Cookie-based DEK-triggered full-logout signal.
 *
 * Why cookies instead of sessionStorage?
 * sessionStorage keys survive the cross-domain navigation chain
 * (app → cognito.amazonaws.com → authdepot → /logout) in Chrome and Firefox,
 * BUT they are cleared by OidcReduxBridge useEffect callbacks that run on the
 * /logout page BEFORE the lazy-loaded Logout.jsx component renders and can read
 * them.  Cookies scoped to the app domain are not touched by any React effect
 * and survive both full-page reloads and cross-domain round-trips.
 *
 * The three-key design (DEK_LP_A/B/C) was a workaround for the per-effect race —
 * it is no longer needed.  A single cookie is sufficient.
 */

const DEK_PENDING_COOKIE = 'dek_logout_pending';
const DEK_REDIRECT_COOKIE = 'dek_post_auth_redirect';
const DEK_TTL_SECONDS = 300; // 5 minutes — prevents stale flags across sessions
const COOKIE_PATH = '/';

function setCookie(name, value, maxAge) {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=${COOKIE_PATH}; max-age=${maxAge}; SameSite=Lax`;
  } catch (e) {
    // ignore
  }
}

function getCookie(name) {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
}

function deleteCookie(name) {
  try {
    document.cookie = `${name}=; path=${COOKIE_PATH}; max-age=0; SameSite=Lax`;
  } catch (e) {
    // ignore
  }
}

/**
 * Sets the DEK-logout-pending cookie and stores the current URL as the
 * post-logout redirect target.  Call this immediately before navigating
 * to the Cognito logout endpoint.
 *
 * @param {string} redirectUrl - URL to return to after ForgeRock re-auth
 */
export function setDekLogoutPending(redirectUrl) {
  setCookie(DEK_PENDING_COOKIE, '1', DEK_TTL_SECONDS);
  if (redirectUrl) {
    setCookie(DEK_REDIRECT_COOKIE, redirectUrl, DEK_TTL_SECONDS);
  }
}

/**
 * Returns true if the DEK-logout-pending cookie is set.
 * Used as an in-flight guard to suppress duplicate dek:session-expired events.
 *
 * @returns {boolean}
 */
export function isDekLogoutPendingActive() {
  return getCookie(DEK_PENDING_COOKIE) === '1';
}

/**
 * Reads and clears the DEK-logout-pending cookie.
 * Returns true if the cookie was set (i.e. a DEK-triggered logout is in progress).
 *
 * Safe to call as a lazy useState initializer — cookies are unaffected by
 * React effects so there is no race with OidcReduxBridge cleanup code.
 *
 * @returns {boolean}
 */
export function readAndClearDekLogoutPending() {
  const active = getCookie(DEK_PENDING_COOKIE) === '1';
  deleteCookie(DEK_PENDING_COOKIE);
  return active;
}

/**
 * Clears the DEK-logout-pending cookie without reading it.
 * Use this when DEK registration succeeds (stale flag is no longer needed)
 * or when performing a full cleanup.
 */
export function clearAllDekLogoutPending() {
  deleteCookie(DEK_PENDING_COOKIE);
}

/**
 * Returns the stored post-DEK-logout redirect URL and clears it.
 * Falls back to the provided default when none is stored.
 *
 * @param {string} fallback - URL to use when no stored redirect is present
 * @returns {string}
 */
export function getDekPostLogoutUrl(fallback) {
  const stored = getCookie(DEK_REDIRECT_COOKIE);
  deleteCookie(DEK_REDIRECT_COOKIE);
  return stored || fallback;
}
